import {
  Button,
  DatePicker,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea,
} from "@heroui/react";
import {
  BuildingOffice2Icon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";
import { CalendarDate, parseDate } from "@internationalized/date";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../utils/toast";
import { CityDropdown } from "../../components/common/cityDropdown";
import { ContriesDropdown } from "../../components/common/countriesDropdown";
import { createEvent, updateEvent } from "../../services/api";
import {
  CreateEventRequest,
  EventDetailResponse,
  UpdateEventRequest,
} from "../../types/types";

interface CreateEditEventProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editEventData?: EventDetailResponse | null;
}

type EventFormErrors = {
  eventName?: string;
  eventCountry?: string;
  eventCity?: string;
  eventVenue?: string;
  startDate?: string;
};

type EventDatePayload = {
  startDate?: string;
};

const acceptedDealStatuses = ["ACCEPTED", "CONFIRMED"];
const acceptedMatchupStatuses = [
  "RED_ACCEPTED",
  "BLUE_ACCEPTED",
  "BOTH_ACCEPTED",
  "CONFIRMED",
];
const EVENT_MINIMUM_ADVANCE_DAYS = 7;

const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getMinimumEventDateLocalString = (): string => {
  const minimumDate = new Date();
  minimumDate.setHours(0, 0, 0, 0);
  minimumDate.setDate(minimumDate.getDate() + EVENT_MINIMUM_ADVANCE_DAYS);

  return formatDateLocal(minimumDate);
};

const toDateLocalValue = (value?: string | null): string => {
  if (!value) return "";

  const trimmedValue = value.trim();
  return trimmedValue.slice(0, 10);
};

const toDatePickerValue = (value: string): CalendarDate | null => {
  if (!value) return null;

  try {
    return parseDate(value.slice(0, 10));
  } catch {
    return null;
  }
};

const fromDatePickerValue = (value: CalendarDate | null): string => {
  return value ? value.toString().slice(0, 10) : "";
};

const hasAcceptedDeal = (eventData?: EventDetailResponse | null): boolean => {
  if (!eventData) return false;
  if ((eventData as any).criticalDetailsEditable === false) return true;

  return (eventData.matchups || []).some((matchup: any) => {
    const matchupStatus = String(matchup?.status || "").toUpperCase();
    const redDealStatus = String(matchup?.redCornerDeal?.status || "").toUpperCase();
    const blueDealStatus = String(matchup?.blueCornerDeal?.status || "").toUpperCase();

    return (
      acceptedMatchupStatuses.includes(matchupStatus) ||
      acceptedDealStatuses.includes(redDealStatus) ||
      acceptedDealStatuses.includes(blueDealStatus)
    );
  });
};

const lockedInputClassNames = {
  inputWrapper: "bg-white border-gray-300 shadow-sm cursor-not-allowed",
  input: "text-gray-900 cursor-not-allowed",
};

const getUsername = (): string => {
  try {
    const userJson = localStorage.getItem("loginResponse");

    if (!userJson) {
      return "";
    }

    const user = JSON.parse(userJson);

    if (user.username) {
      return user.username;
    }

    if (user.email) {
      return user.email.split("@")[0];
    }

    return "";
  } catch {
    return "";
  }
};

const getErrorMessage = (error: any, fallbackMessage: string): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.message) {
    return error.message;
  }

  return fallbackMessage;
};

const CreateEditEvent = ({
  isOpen,
  onClose,
  onSuccess,
  editEventData,
}: CreateEditEventProps) => {
  const { t } = useTranslation("events");
  const minDate = getMinimumEventDateLocalString();
  const minimumSelectableDate = toDatePickerValue(minDate);

  const [eventName, setEventName] = useState("");
  const [eventDescription, setEventDescription] = useState("");
  const [eventVenue, setEventVenue] = useState("");
  const [eventCountry, setEventCountry] = useState("");
  const [eventCity, setEventCity] = useState("");
  const [startDate, setStartDate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<EventFormErrors>({});
  const freezeCriticalFields = isOpen && hasAcceptedDeal(editEventData);

  const resetForm = () => {
    setEventName("");
    setEventDescription("");
    setEventVenue("");
    setEventCountry("");
    setEventCity("");
    setStartDate("");
    setErrors({});
  };

  useEffect(() => {
    if (!editEventData) {
      setEventName("");
      setEventDescription("");
      setEventVenue("");
      setEventCountry("");
      setEventCity("");
      setStartDate("");
      setErrors({});
      return;
    }

    setEventName(editEventData.name || "");
    setEventDescription(editEventData.description || "");
    setEventVenue(editEventData.venue || "");
    setEventCountry(editEventData.country || editEventData.location || "");
    setEventCity(editEventData.city || "");
    setStartDate(
      toDateLocalValue(
        (editEventData as any).startDate || editEventData.startDateTime
      )
    );
    setErrors({});
  }, [editEventData, isOpen]);

  const clearError = (field: keyof EventFormErrors) => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = (): boolean => {
    const nextErrors: EventFormErrors = {};

    if (!eventName.trim()) {
      nextErrors.eventName = t("event_name_required", "Event name is required");
    }

    if (!freezeCriticalFields) {
      if (!eventCountry.trim()) {
        nextErrors.eventCountry = t("country_required", "Country is required");
      }

      if (!eventCity.trim()) {
        nextErrors.eventCity = t("city_required", "City is required");
      }

      if (!eventVenue.trim()) {
        nextErrors.eventVenue = t("venue_required", "Venue is required");
      }

      const currentMinimumDate = getMinimumEventDateLocalString();

      if (!startDate) {
        nextErrors.startDate = t(
          "event_date_required",
          "Event date is required"
        );
      } else if (startDate < currentMinimumDate) {
        nextErrors.startDate = t(
          "event_date_minimum_advance",
          "Event must be scheduled at least 7 days in advance."
        );
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleCountryChange = (country: string) => {
    setEventCountry(country);
    setEventCity("");
    clearError("eventCountry");
    clearError("eventCity");
  };

  const handleCityChange = (city: string) => {
    setEventCity(city);
    clearError("eventCity");
  };

  const handleDateChange = (value: CalendarDate | null) => {
    if (freezeCriticalFields) {
      return;
    }

    const selectedDate = fromDatePickerValue(value);

    if (selectedDate && selectedDate < minDate) {
      setErrors((previousErrors) => ({
        ...previousErrors,
        startDate: t(
          "event_date_minimum_advance",
          "Event must be scheduled at least 7 days in advance."
        ),
      }));
      return;
    }

    setStartDate(selectedDate);
    clearError("startDate");
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;

    const username = getUsername();
    setIsSubmitting(true);

    try {
      if (editEventData) {
        const updatePayload: UpdateEventRequest & EventDatePayload = {
          name: eventName.trim(),
          description: eventDescription.trim(),
        };

        if (!freezeCriticalFields) {
          updatePayload.venue = eventVenue.trim();
          updatePayload.country = eventCountry.trim();
          updatePayload.city = eventCity.trim();
          updatePayload.startDate = startDate || undefined;
        }

        await updateEvent(username, editEventData.id, updatePayload);
        toast.success(t("event_updated_successfully", "Event updated successfully"));
      } else {
        const createPayload: CreateEventRequest & EventDatePayload = {
          name: eventName.trim(),
          description: eventDescription.trim(),
          venue: eventVenue.trim(),
          country: eventCountry.trim(),
          city: eventCity.trim(),
          startDate: startDate || undefined,
        };

        await createEvent(username, createPayload);
        toast.success(t("event_created_successfully", "Event created successfully"));
      }

      resetForm();
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("failed_to_save_event", "Failed to save event. Please try again.")
      );

      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isEditMode = Boolean(editEventData);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isDismissable={false}
      scrollBehavior="inside"
      size="2xl"
      placement="center"
      classNames={{
        wrapper: "items-center justify-center p-2 sm:p-4",
        base: "bg-white rounded-3xl shadow-[0px_14px_28px_0px_rgba(0,0,0,0.08)] w-full max-w-[calc(100vw-1.5rem)] sm:max-w-[640px] max-h-[90dvh] sm:max-h-[90vh] my-auto overflow-hidden font-['Plus_Jakarta_Sans',sans-serif]",
        header: "px-5 sm:px-8 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b border-gray-100 shrink-0",
        body: "px-5 sm:px-8 py-4 sm:py-6 space-y-3.5 sm:space-y-5 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
        footer: "px-5 sm:px-8 py-3.5 sm:pb-6 sm:pt-4 border-t border-gray-100 shrink-0",
      }}
    >
      <ModalContent>
        <ModalHeader>
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 font-['Plus_Jakarta_Sans',sans-serif]">
              {isEditMode
                ? t("edit_event", "Edit Event")
                : t("create_new_event", "Create New Event")}
            </h2>
            <p className="text-xs sm:text-sm font-normal text-gray-500 mt-0.5 sm:mt-1 font-['Plus_Jakarta_Sans',sans-serif]">
              {isEditMode
                ? t("edit_event_subtitle", "Update combat sports event details")
                : t("create_event_subtitle", "Schedule and announce a new combat sports event")}
            </p>
          </div>
        </ModalHeader>

        <ModalBody>
          <div className="space-y-1 sm:space-y-1.5">
            <label className="text-xs sm:text-sm font-medium text-gray-700 block font-['Plus_Jakarta_Sans',sans-serif]">
              {t("event_name", "Event Name")} <span className="text-blue-600">*</span>
            </label>
            <Input
              placeholder={t("event_name_placeholder", "e.g. Fight Night")}
              value={eventName}
              onValueChange={(value) => {
                setEventName(value);
                clearError("eventName");
              }}
              size="sm"
              variant="bordered"
              radius="lg"
              classNames={{
                inputWrapper: "border-gray-200 hover:border-gray-300 focus-within:!border-blue-500",
                input: "text-sm font-normal text-gray-900 placeholder:text-gray-400 font-['Plus_Jakarta_Sans',sans-serif]",
              }}
            />
            {errors.eventName && (
              <p className="text-xs leading-4 text-red-500 font-['Plus_Jakarta_Sans',sans-serif]">{errors.eventName}</p>
            )}
          </div>

          <div className="space-y-1 sm:space-y-1.5">
            <label className="text-xs sm:text-sm font-medium text-gray-700 block font-['Plus_Jakarta_Sans',sans-serif]">
              {t("description", "Description")}
            </label>
            <Textarea
              placeholder={t("description_placeholder", "Event background, special rules, or main attraction notes...")}
              value={eventDescription}
              onValueChange={setEventDescription}
              size="sm"
              variant="bordered"
              radius="lg"
              minRows={2}
              maxRows={4}
              classNames={{
                inputWrapper: "border-gray-200 hover:border-gray-300 focus-within:!border-blue-500",
                input: "text-sm font-normal text-gray-900 placeholder:text-gray-400 font-['Plus_Jakarta_Sans',sans-serif]",
              }}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.12fr)] gap-3 sm:gap-4">
            <div className="space-y-1 sm:space-y-1.5 min-w-0">
              <label className="text-xs sm:text-sm font-medium text-gray-700 block font-['Plus_Jakarta_Sans',sans-serif]">
                {t("country", "Country")} <span className="text-blue-600">*</span>
              </label>
              {freezeCriticalFields ? (
                <Input
                  value={eventCountry}
                  isReadOnly
                  size="sm"
                  variant="bordered"
                  radius="lg"
                  startContent={<GlobeAltIcon className="h-4 w-4 text-gray-400" />}
                  classNames={lockedInputClassNames}
                />
              ) : (
                <ContriesDropdown
                  value={eventCountry}
                  onLocationChange={handleCountryChange}
                />
              )}
              {errors.eventCountry && (
                <p className="text-xs leading-4 text-red-500 font-['Plus_Jakarta_Sans',sans-serif]">{errors.eventCountry}</p>
              )}
            </div>

            <div className="space-y-1 sm:space-y-1.5 min-w-0">
              <label className="text-xs sm:text-sm font-medium text-gray-700 block font-['Plus_Jakarta_Sans',sans-serif]">
                {t("city", "City")} <span className="text-blue-600">*</span>
              </label>
              {freezeCriticalFields ? (
                <Input
                  value={eventCity}
                  isReadOnly
                  size="sm"
                  variant="bordered"
                  radius="lg"
                  startContent={<BuildingOffice2Icon className="h-4 w-4 text-gray-400" />}
                  classNames={lockedInputClassNames}
                />
              ) : (
                <CityDropdown
                  country={eventCountry}
                  value={eventCity}
                  onCityChange={handleCityChange}
                />
              )}
              {errors.eventCity && (
                <p className="text-xs leading-4 text-red-500 font-['Plus_Jakarta_Sans',sans-serif]">{errors.eventCity}</p>
              )}
            </div>

            <div className="space-y-1 sm:space-y-1.5 min-w-0">
              <label className="text-xs sm:text-sm font-medium text-gray-700 block font-['Plus_Jakarta_Sans',sans-serif]">
                {t("venue", "Venue")} <span className="text-blue-600">*</span>
              </label>
              <Input
                placeholder={t("venue_placeholder", "e.g. Salem Indoor Stadium")}
                value={eventVenue}
                onValueChange={(value) => {
                  setEventVenue(value);
                  clearError("eventVenue");
                }}
                isReadOnly={freezeCriticalFields}
                size="sm"
                variant="bordered"
                radius="lg"
                className="min-w-0"
                classNames={{
                  inputWrapper: `min-w-0 border-gray-200 hover:border-gray-300 focus-within:!border-blue-500 ${
                    freezeCriticalFields ? "cursor-not-allowed bg-white" : ""
                  }`,
                  input: `truncate text-sm font-normal text-gray-900 font-['Plus_Jakarta_Sans',sans-serif] ${
                    freezeCriticalFields ? "cursor-not-allowed" : ""
                  }`,
                }}
              />
              {errors.eventVenue && (
                <p className="text-xs leading-4 text-red-500 font-['Plus_Jakarta_Sans',sans-serif]">{errors.eventVenue}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-1 sm:space-y-1.5">
              <label className="text-xs sm:text-sm font-medium text-gray-700 block font-['Plus_Jakarta_Sans',sans-serif]">
                {t("event_date", "Event Date")} <span className="text-blue-600">*</span>
              </label>
              <DatePicker
                value={toDatePickerValue(startDate)}
                isDateUnavailable={(date) =>
                  minimumSelectableDate
                    ? date.compare(minimumSelectableDate) < 0
                    : false
                }
                aria-label={t("select_event_date", "Select event date")}
                showMonthAndYearPickers
                size="sm"
                variant="bordered"
                radius="lg"
                calendarProps={{
                  classNames: {
                    cellButton:
                      "data-[unavailable=true]:text-gray-300 data-[unavailable=true]:opacity-60 data-[unavailable=true]:!no-underline data-[unavailable=true]:[text-decoration:none]",
                  },
                }}
                className={`w-full ${freezeCriticalFields ? "cursor-not-allowed" : ""}`}
                classNames={{
                  base: freezeCriticalFields ? "cursor-not-allowed" : "",
                  inputWrapper: freezeCriticalFields
                    ? "cursor-not-allowed bg-white border-gray-200"
                    : "border-gray-200 hover:border-gray-300 focus-within:!border-blue-500",
                  input: freezeCriticalFields
                    ? "cursor-not-allowed text-sm font-normal text-gray-900"
                    : "text-sm font-normal text-gray-900",
                }}
                isReadOnly={freezeCriticalFields}
                onChange={handleDateChange}
              />
              {errors.startDate && (
                <p className="text-xs leading-4 text-red-500 font-['Plus_Jakarta_Sans',sans-serif]">{errors.startDate}</p>
              )}
            </div>
          </div>
        </ModalBody>

        <ModalFooter className="flex items-center justify-end gap-2.5 sm:gap-3">
          <Button
            variant="light"
            size="sm"
            onPress={handleClose}
            disabled={isSubmitting}
            className="flex-1 sm:flex-none px-4 sm:px-5 py-2 rounded-full text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 font-['Plus_Jakarta_Sans',sans-serif]"
          >
            {t("cancel", "Cancel")}
          </Button>

          <Button
            color="primary"
            size="sm"
            className="flex-1 sm:flex-none px-5 py-2 rounded-full text-sm font-bold bg-blue-600 hover:bg-blue-700 text-white font-['Plus_Jakarta_Sans',sans-serif]"
            onPress={handleFormSubmit}
            isLoading={isSubmitting}
          >
            {isEditMode
              ? t("save_changes", "Save Changes")
              : t("create_event", "Create Event")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateEditEvent;